export type DeploymentEnvironment = 'local' | 'dev' | 'qa' | 'prod'

export interface EnvironmentConfig {
  redirectUri: string
  postgrestBaseUrl: string
  lambdaAuthUrl: string
  canvasAuthBaseUrl: string
  swaggerPostgrestJwtLambdaName: string
  uploadToS3LambdaName: string
  generateDownloadUrlLambdaName: string
  enableEmbeddedDebugLogging: boolean
  /** Terraform output: outbound_syncs_api_url */
  outboundSyncsApiUrl: string
  /** Terraform output: outbound_syncs_websocket_url */
  outboundSyncsWebsocketUrl: string
  /** Interactive repayment-export API. Jobs and files are durable server resources. */
  repaymentExportsApiUrl: string
}

const REDIRECT_PATH = '/uploaded-files'
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1'])
const DEV_HOSTS = new Set(['dev-repayments.365business.finance'])
const QA_HOSTS = new Set(['qa-repayments.365business.finance'])
const PROD_HOSTS = new Set(['repayments.365business.finance'])

const BASE_CONFIG: Record<DeploymentEnvironment, EnvironmentConfig> = {
  local: {
    redirectUri: `http://localhost:4200${REDIRECT_PATH}`,
    postgrestBaseUrl: 'https://dev-postgrest.365business.finance',
    lambdaAuthUrl:
      'https://v98kjj8ajc.execute-api.eu-west-2.amazonaws.com/devtwo/repaymentsLambdaAuthProxy-dev',
    canvasAuthBaseUrl:
      'https://dev-repayments.embed.365business.finance/embed-view-auth',
    swaggerPostgrestJwtLambdaName:
      'repayments-swaggerPostgrestJwt-lambda-dev-tf',
    uploadToS3LambdaName: 'repayments-frontendUpload-lambda-dev-tf',
    generateDownloadUrlLambdaName: 'repayments-frontendDownload-lambda-dev-tf',
    enableEmbeddedDebugLogging: true,
    outboundSyncsApiUrl: '',
    // Terraform dev output: outbound_syncs_websocket_url. This shared socket
    // also broadcasts uploaded-files.changed for partner settlement imports.
    outboundSyncsWebsocketUrl: 'wss://m43dgcqod6.execute-api.eu-west-2.amazonaws.com/dev',
    repaymentExportsApiUrl:
      'https://fwdgycbp62z4tggxbadzxorr740yijwk.lambda-url.eu-west-2.on.aws/repayment-exports',
  },
  dev: {
    redirectUri: `https://dev-repayments.365business.finance${REDIRECT_PATH}`,
    postgrestBaseUrl: 'https://dev-postgrest.365business.finance',
    lambdaAuthUrl:
      'https://v98kjj8ajc.execute-api.eu-west-2.amazonaws.com/devtwo/repaymentsLambdaAuthProxy-dev',
    canvasAuthBaseUrl:
      'https://dev-repayments.embed.365business.finance/embed-view-auth',
    swaggerPostgrestJwtLambdaName:
      'repayments-swaggerPostgrestJwt-lambda-dev-tf',
    uploadToS3LambdaName: 'repayments-frontendUpload-lambda-dev-tf',
    generateDownloadUrlLambdaName: 'repayments-frontendDownload-lambda-dev-tf',
    enableEmbeddedDebugLogging: true,
    outboundSyncsApiUrl: '',
    // Terraform dev output: outbound_syncs_websocket_url. This shared socket
    // also broadcasts uploaded-files.changed for partner settlement imports.
    outboundSyncsWebsocketUrl: 'wss://m43dgcqod6.execute-api.eu-west-2.amazonaws.com/dev',
    repaymentExportsApiUrl:
      'https://fwdgycbp62z4tggxbadzxorr740yijwk.lambda-url.eu-west-2.on.aws/repayment-exports',
  },
  qa: {
    redirectUri: `https://qa-repayments.365business.finance${REDIRECT_PATH}`,
    postgrestBaseUrl: 'https://qa-postgrest.365business.finance',
    lambdaAuthUrl:
      'https://33zkxuydg75n2p2ivjtfcnkjrm0vnhql.lambda-url.eu-west-3.on.aws/',
    canvasAuthBaseUrl:
      'https://qa-repayments.embed.365business.finance/embed-view-auth',
    swaggerPostgrestJwtLambdaName:
      'repayments-swaggerPostgrestJwt-lambda-qa-tf',
    uploadToS3LambdaName: 'repayments-frontendUpload-lambda-qa-tf',
    generateDownloadUrlLambdaName: 'repayments-frontendDownload-lambda-qa-tf',
    enableEmbeddedDebugLogging: true,
    outboundSyncsApiUrl: '',
    outboundSyncsWebsocketUrl: '',
    repaymentExportsApiUrl:
      'https://33zkxuydg75n2p2ivjtfcnkjrm0vnhql.lambda-url.eu-west-3.on.aws/repayment-exports',
  },
  prod: {
    redirectUri: `https://repayments.365business.finance${REDIRECT_PATH}`,
    postgrestBaseUrl: 'https://postgrest.365business.finance',
    lambdaAuthUrl:
      'https://pzgixduw2greh5dppqumbciwoq0nvtet.lambda-url.eu-west-2.on.aws/',
    canvasAuthBaseUrl:
      'https://repayments.embed.365business.finance/embed-view-auth',
    swaggerPostgrestJwtLambdaName:
      'repayments-swaggerPostgrestJwt-lambda-prod-tf',
    uploadToS3LambdaName: 'repayments-frontendUpload-lambda-prod-tf',
    generateDownloadUrlLambdaName: 'repayments-frontendDownload-lambda-prod-tf',
    enableEmbeddedDebugLogging: false,
    outboundSyncsApiUrl: 'https://pzgixduw2greh5dppqumbciwoq0nvtet.lambda-url.eu-west-2.on.aws/outbound-syncs',
    outboundSyncsWebsocketUrl: 'wss://dcz4k60tc0.execute-api.eu-west-2.amazonaws.com/prod',
    repaymentExportsApiUrl: 'https://pzgixduw2greh5dppqumbciwoq0nvtet.lambda-url.eu-west-2.on.aws/repayment-exports',
  },
}

export const getAppConfig = (env: DeploymentEnvironment): EnvironmentConfig =>
  BASE_CONFIG[env]

const isLocalHost = (hostname: string): boolean => {
  const normalized = hostname.toLowerCase()
  return (
    LOCAL_HOSTS.has(normalized) ||
    normalized.endsWith('.localhost') ||
    normalized.endsWith('.local')
  )
}

export const detectDeploymentEnvironment = (): DeploymentEnvironment => {
  if (typeof window === 'undefined') {
    return 'local'
  }

  const hostname = window.location.hostname.toLowerCase()

  if (isLocalHost(hostname)) {
    return 'local'
  }

  if (DEV_HOSTS.has(hostname)) {
    return 'dev'
  }

  if (QA_HOSTS.has(hostname)) {
    return 'qa'
  }

  if (PROD_HOSTS.has(hostname)) {
    return 'prod'
  }

  return 'prod'
}
